// src/hooks/useQuotations.js
// Single source of truth for all quotation + bill logic, shared between the
// desktop dashboard (Dashboard.jsx) and the new mobile admin dashboard
// (MobileAdminDashboard.jsx). Centralizes:
//   • subscribing to quotations + quotation settings
//   • filtering / pagination of the quotation list
//   • create / update / delete
//   • print + WhatsApp (PDF) sending
//   • the post-save print prompt state

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { B } from "../components/UI";
import {
  subscribeQuotationSettings, fetchQuotations, fetchQuotationsForDateRange,
  rebuildQuotationCounters, bumpQuotationCounters, mergeQuotationCounters,
  addQuotation, updateQuotation, deleteQuotation,
  addCustomer, updateCustomer, saveQuotationSettings, addTransport,
  fetchTransports,
} from "../firebase/services";
import { logActivity } from "../firebase/auth";
import { printQuotationDocument } from "../pages/admin/print/quotationPrint";
import { printTransportSlip } from "../pages/admin/print/transportPrint";
import { generateQuotationPdf, blobToBase64 } from "../utils/pdf";
import { generateUpiQrImage } from "../utils/upiQr";
import { getGSTValidationMessage, getMobileValidationMessage } from "../utils/validation";
import { getTransportDisplayName, getTransportName, findTransportConflict, CUSTOM_TRANSPORT_ID } from "../utils/transport";

// ─── Shared formatting helpers ────────────────────────────────────────────────
export const formatDateTime = (value) => {
  if (!value) return "—";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatCurrency = (amount) =>
  `₹${Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const getDateInputValue = (value) => {
  if (!value) return "";
  const date = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return localDate.toISOString().slice(0, 10);
};

const toTitleCase = (value) =>
  String(value || "").replace(/\b\w/g, char => char.toUpperCase());

const normalizeCustomerText = (value) => String(value || "").trim().toLowerCase();

const QUOTATION_PAGE_SIZE = 10;

// Build Firestore boundary Dates for a From/To date range ("YYYY-MM-DD").
const buildDateRange = (fromDate, toDate) => {
  if (!fromDate && !toDate) return null;
  const makeStart = (value) => {
    const [y, m, d] = String(value).split("-").map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  };
  const makeEnd = (value) => {
    const [y, m, d] = String(value).split("-").map(Number);
    return new Date(y, m - 1, d, 23, 59, 59, 999);
  };
  return {
    from: fromDate ? makeStart(fromDate) : null,
    to: toDate ? makeEnd(toDate) : null,
  };
};

/**
 * Manage the full quotation lifecycle.
 *
 * @param {Object} deps
 * @param {Object}   deps.user        Firebase auth user (for WhatsApp API idToken)
 * @param {Object}   deps.profile     Admin Firestore record
 * @param {Object}   deps.permissions Permissions object
 * @param {Object}   deps.branding    Branding object (for print / PDF)
 * @param {Array}    deps.designs     Catalog designs
 * @param {Array}    deps.customers    Customers list
 * @param {Function} deps.showToast    (msg, color?) => void
 * @param {Function} deps.setCrudBusy  (busy: boolean) => void
 * @param {Function} deps.confirm      (options) => Promise<boolean>
 */
export default function useQuotations({
  user, profile, permissions, branding, designs = [], customers = [],
  showToast, setCrudBusy, confirm,
}) {
  const [quotations, setQuotations] = useState([]);
  const [quotationSettings, setQuotationSettings] = useState({ allowPriceEdit: false, allowDiscount: false, allowDiscountInvoice: false, defaultDiscountQuotation: 0, defaultDiscountInvoice: 0, minOrderValueQuotation: 0, minOrderValueInvoice: 0, showFirmName: false, showPackagingCharges: false, fetchDesignDetails: false });
  const [quotationSaving, setQuotationSaving] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [upiQrSending, setUpiQrSending] = useState(false);
  const [quotationsLoading, setQuotationsLoading] = useState(false);
  // Counters computed in this session (bootstrap / after save / after delete);
  // merged with the copy stored in settings/quotation.
  const [localCounters, setLocalCounters] = useState(null);
  const [quotationSettingsLoaded, setQuotationSettingsLoaded] = useState(false);

  const [viewQuotation, setViewQuotation] = useState(null);
  const [editQuotation, setEditQuotation] = useState(null);
  const [quotationPrintPrompt, setQuotationPrintPrompt] = useState(null);

  const [quotationSearch, setQuotationSearch] = useState("");
  const [quotationCustomerFilter, setQuotationCustomerFilter] = useState("all");
  const [quotationTypeFilter, setQuotationTypeFilter] = useState("all");
  const [quotationDateFrom, setQuotationDateFrom] = useState(() => getDateInputValue(new Date()));
  const [quotationDateTo, setQuotationDateTo] = useState(() => getDateInputValue(new Date()));
  const [quotationPage, setQuotationPage] = useState(1);

  useEffect(() => {
    const u6 = subscribeQuotationSettings((settings) => {
      setQuotationSettings(settings);
      setQuotationSettingsLoaded(true);
    });
    return () => u6();
  }, []);

  // Quotations: fetch only the selected day/range from the server instead of
  // subscribing to (and re-reading) the entire multi-month collection. If no
  // date filter is set (explicit "show all dates") we fall back to a full fetch.
  const loadQuotations = useCallback(async () => {
    const range = buildDateRange(quotationDateFrom, quotationDateTo);
    const list = range ? await fetchQuotationsForDateRange(range) : await fetchQuotations();
    setQuotations(list);
  }, [quotationDateFrom, quotationDateTo]);

  useEffect(() => {
    let active = true;
    setQuotationsLoading(true);
    (async () => {
      try {
        const range = buildDateRange(quotationDateFrom, quotationDateTo);
        const list = range ? await fetchQuotationsForDateRange(range) : await fetchQuotations();
        if (active) setQuotations(list);
      } finally {
        if (active) setQuotationsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [quotationDateFrom, quotationDateTo]);

  // Auto quotation/bill numbering. Instead of reading every quotation ever
  // created on each admin load, use the running maxima stored in
  // settings/quotation.numberCounters (delivered by the settings listener above).
  // Only if they don't exist yet is a one-off full scan done to create them.
  const countersBootstrapRef = useRef(false);
  useEffect(() => {
    if (!quotationSettingsLoaded || quotationSettings?.numberCounters) return;
    if (countersBootstrapRef.current) return;
    countersBootstrapRef.current = true;
    let active = true;
    (async () => {
      try {
        const counters = await rebuildQuotationCounters();
        if (active) setLocalCounters(counters);
      } catch (e) { /* numbers stay as they are; next load retries */ }
    })();
    return () => { active = false; };
  }, [quotationSettingsLoaded, quotationSettings?.numberCounters]);

  // After a save, raise the counters; after a delete (savedQuotation = null),
  // rebuild them exactly so a deleted latest number can be reused as before.
  const syncQuotationCounters = useCallback(async (savedQuotation) => {
    try {
      const counters = savedQuotation
        ? await bumpQuotationCounters(savedQuotation)
        : await rebuildQuotationCounters();
      setLocalCounters(prev => (savedQuotation ? mergeQuotationCounters(prev, counters) : counters));
    } catch (e) {
      // Fall back to an exact scan so the next number is never stale.
      try { setLocalCounters(await rebuildQuotationCounters()); } catch (_) { /* keep current */ }
    }
  }, []);

  const numberCounters = useMemo(
    () => mergeQuotationCounters(quotationSettings?.numberCounters, localCounters),
    [quotationSettings?.numberCounters, localCounters]
  );

  // The quotation builder derives "next number" from a list of quotations
  // (max quotation_number, max bill_number in the current financial year).
  // Feed it one synthetic entry per counter so its logic stays unchanged.
  const quotationNumbers = useMemo(() => {
    if (!numberCounters) return [];
    const list = [{ quotation_number: String(numberCounters.quotationMax || 0), bill_number: "", created_at: null }];
    for (const [fy, max] of Object.entries(numberCounters.billMaxByFy || {})) {
      // 1 July always falls inside the financial year that starts in April of `fy`.
      list.push({ quotation_number: "", bill_number: String(max), created_at: new Date(Number(fy), 6, 1) });
    }
    return list;
  }, [numberCounters]);

  const canManageQuotations = permissions?.canManageQuotations ?? permissions?.canManageCustomers;

  // ── Filtering / pagination ──────────────────────────────────────────────────
  const quotationSearchTerm = quotationSearch.trim().toLowerCase();
  const filteredQuotations = quotations
    .filter((quotation) => {
      const matchesSearch = !quotationSearchTerm || [quotation.quotation_number, quotation.customer_name, quotation.grand_total]
        .some(field => String(field || "").toLowerCase().includes(quotationSearchTerm));
      if (!matchesSearch) return false;
      if (quotationCustomerFilter !== "all" && quotation.customer_id !== quotationCustomerFilter) return false;
      if (quotationTypeFilter === "bills" && !quotation.bill_number) return false;
      if (quotationTypeFilter === "quotations" && quotation.bill_number) return false;
      if (quotationDateFrom) {
        const createdDate = getDateInputValue(quotation.created_at);
        if (createdDate < quotationDateFrom) return false;
      }
      if (quotationDateTo) {
        const createdDate = getDateInputValue(quotation.created_at);
        if (createdDate > quotationDateTo) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const aTime = a.created_at?.toDate ? a.created_at.toDate().getTime() : new Date(a.created_at).getTime();
      const bTime = b.created_at?.toDate ? b.created_at.toDate().getTime() : new Date(b.created_at).getTime();
      return bTime - aTime; // newest first
    });
  const quotationTotalPages = Math.max(1, Math.ceil(filteredQuotations.length / QUOTATION_PAGE_SIZE));
  const paginatedQuotations = filteredQuotations.slice((quotationPage - 1) * QUOTATION_PAGE_SIZE, quotationPage * QUOTATION_PAGE_SIZE);

  useEffect(() => { setQuotationPage(1); }, [quotationSearch, quotationCustomerFilter, quotationTypeFilter, quotationDateFrom, quotationDateTo]);

  // ── Customer matching ───────────────────────────────────────────────────────
  const findExistingCustomer = (customerPayload = {}) => {
    const normalizedMobile = normalizeCustomerText(customerPayload.mobile_number);
    const normalizedName = normalizeCustomerText(customerPayload.customer_name);
    const normalizedFirm = normalizeCustomerText(customerPayload.firm_name);
    if (!normalizedMobile && !normalizedName) return null;

    return customers.find(existing => {
      if (existing.status === "deleted") return false;
      const existingMobile = normalizeCustomerText(existing.mobile_number);
      const existingName = normalizeCustomerText(existing.customer_name);
      const existingFirm = normalizeCustomerText(existing.firm_name);
      if (normalizedMobile && existingMobile && normalizedMobile === existingMobile) return true;
      if (normalizedName && existingName && normalizedName === existingName) {
        if (!normalizedFirm) return true;
        return normalizedFirm === existingFirm;
      }
      return false;
    }) || null;
  };

  // ── WhatsApp → UPI payment-QR image sending ─────────────────────────────────
  // Generates a dynamic `upi://pay` QR (amount + invoice number baked in, brand
  // logo centered) in the browser and posts it to the send-upi-qr route, which
  // uploads it transiently to Anekant and delivers it through the approved
  // image-header template. The image is never stored (no persistence).
  const handleSendUpiQr = async ({ quotation, customerMobile, customerName, invoiceNumber, amount }) => {
    const invoice = String(invoiceNumber || "").trim();
    if (!invoice) {
      showToast("UPI QR can only be sent for invoices (invoice number required)", B.red);
      return false;
    }
    if (!customerMobile) {
      showToast("Customer mobile number is required to send UPI QR", B.red);
      return false;
    }
    if (!user) {
      showToast("You must be signed in to send WhatsApp messages", B.red);
      return false;
    }
    if (!(Number(amount) > 0)) {
      showToast("Invoice amount must be greater than zero", B.red);
      return false;
    }
    if (!String(branding?.upiId || "").trim()) {
      showToast("UPI ID (VPA) is not configured — add it in Branding settings", B.red);
      return false;
    }

    setUpiQrSending(true);
    try {
      const qrBlob = await generateUpiQrImage({ amount, invoiceNumber: invoice, branding });
      const qrImageBase64 = await blobToBase64(qrBlob);

      const idToken = await user.getIdToken();
      const apiUrl = process.env.REACT_APP_PRODUCTS_API_URL || "https://products.nilamsynthetics.com";
      const response = await fetch(`${apiUrl}/api/whatsapp/send-upi-qr`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          quotationId: quotation?.id || "",
          customerName,
          customerMobile,
          docNumber: invoice,
          amount: Number(amount) || 0,
          qrImageBase64,
          filename: `UPI_QR_${invoice}.png`,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        const fallback = "Failed to send UPI QR on WhatsApp";
        const errMsg = result?.error || fallback;
        const code = result?.code;
        const step = result?.step;
        let hint = "";
        if (step === "media:upload" || step === "media:verify") {
          hint = " The QR image upload/verification failed — please retry.";
        } else if (String(code) === "131026") {
          hint = " WhatsApp requires the recipient to have messaged this number within the last 24h before a template can be delivered.";
        } else if (String(code) === "1" || String(errMsg).toLowerCase().includes("internal error")) {
          hint = " WhatsApp returned an internal/transient error. Check send limits and retry — see whatsappLogs for the full code.";
        }
        throw new Error(errMsg + hint);
      }

      showToast("UPI QR sent ✓", B.green);
      await logActivity(profile?.uid, "whatsapp_send", `Sent UPI payment QR for invoice ${invoice} via WhatsApp`);
      return true;
    } catch (e) {
      console.error("[useQuotations] UPI QR send error:", e);
      showToast(e.message || "Failed to send UPI QR on WhatsApp", B.red);
      return false;
    } finally {
      setUpiQrSending(false);
    }
  };

  // Standalone "Send UPI QR" action from the quotations list (with confirm).
  const handleSendUpiQrFromList = async (quotation) => {
    const invoiceNumber = String(quotation.bill_number || "").trim();
    if (!invoiceNumber) {
      showToast("UPI QR can only be sent for invoices (invoice number required)", B.red);
      return;
    }
    if (!quotation.customer_mobile) {
      showToast("Customer mobile number is required to send UPI QR", B.red);
      return;
    }

    const confirmed = await confirm({
      title: "Send UPI QR?",
      message: `Send the payment QR for invoice #${invoiceNumber} (${formatCurrency(quotation.grand_total || 0)}) to ${quotation.customer_name || "customer"} (${quotation.customer_mobile})?`,
      confirmLabel: "Send UPI QR",
    });
    if (!confirmed) return;

    await handleSendUpiQr({
      quotation,
      customerMobile: quotation.customer_mobile,
      customerName: quotation.customer_name || "Customer",
      invoiceNumber,
      amount: quotation.grand_total || 0,
    });
  };

  // ── WhatsApp → PDF sending ──────────────────────────────────────────────────
  const handleSendWhatsApp = async ({ quotation, customerMobile, customerName, docType, docNumber, amount, totalPieces, date }) => {
    if (!customerMobile) {
      showToast("Customer mobile number is required to send WhatsApp message", B.red);
      return;
    }
    if (!user) {
      showToast("You must be signed in to send WhatsApp messages", B.red);
      return;
    }

    setWhatsappSending(true);
    try {
      const pdfBlob = await generateQuotationPdf({ quotation, customers, designs, branding, showPackagingCharges: Boolean(quotationSettings?.showPackagingCharges) });
      const pdfBase64 = await blobToBase64(pdfBlob);

      const idToken = await user.getIdToken();
      const apiUrl = process.env.REACT_APP_PRODUCTS_API_URL || "https://products.nilamsynthetics.com";
      const response = await fetch(`${apiUrl}/api/whatsapp/send-quotation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          quotationId: quotation.id || "",
          customerName,
          customerMobile,
          docType,
          docNumber,
          amount,
          totalPieces,
          date,
          pdfBase64,
          filename: `${docType}_${docNumber}_${String(customerName || "Customer").replace(/\s+/g, "")}.pdf`,
        }),
      });

      const result = await response.json();
      if (!response.ok) {
        const fallback = "Failed to send WhatsApp message";
        const errMsg = result?.error || fallback;
        const code = result?.code;
        const step = result?.step;
        let hint = "";
        if (step === "media:upload" || step === "media:verify") {
          hint = " The WhatsApp PDF upload/verification failed — please retry or try a smaller file.";
        } else if (String(code) === "131026") {
          hint = " WhatsApp requires the recipient to have messaged this number within the last 24h before a template can be delivered.";
        } else if (String(code) === "1" || String(errMsg).toLowerCase().includes("internal error")) {
          hint = " WhatsApp returned an internal/transient error. Check send limits and retry — see whatsappLogs for the full code.";
        }
        throw new Error(errMsg + hint);
      }

      showToast(`WhatsApp ${docType.toLowerCase()} sent ✓`, B.green);
      await logActivity(profile?.uid, "whatsapp_send", `Sent ${docType} ${docNumber} via WhatsApp`);

      // Auto-send the payment QR image right after the invoice notification
      // goes out (invoices only). A failure here surfaces its own toast and
      // never fails the invoice send itself.
      if (docType === "Invoice" && Number(amount) > 0) {
        try {
          await handleSendUpiQr({ quotation, customerMobile, customerName, invoiceNumber: docNumber, amount });
        } catch (chainErr) {
          console.error("[useQuotations] Auto UPI QR send error:", chainErr);
        }
      }
    } catch (e) {
      console.error("[useQuotations] WhatsApp send error:", e);
      showToast(e.message || "Failed to send WhatsApp message", B.red);
    } finally {
      setWhatsappSending(false);
    }
  };

  const handleSendWhatsAppFromList = async (quotation) => {
    if (!quotation.customer_mobile) {
      showToast("Customer mobile number is required to send WhatsApp message", B.red);
      return;
    }
    const hasBillNumber = Boolean(String(quotation.bill_number || "").trim());
    const docType = hasBillNumber ? "Invoice" : "Quotation";
    const docNumber = hasBillNumber ? String(quotation.bill_number || "").trim() : String(quotation.quotation_number || "").trim();
    const totalPieces = (quotation.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    const date = new Date().toLocaleDateString("en-GB");

    const confirmed = await confirm({
      title: `Send ${docType}?`,
      message: `Send ${docType} #${docNumber} to ${quotation.customer_name || "customer"} (${quotation.customer_mobile}) via WhatsApp?`,
      confirmLabel: `Send ${docType}`,
    });
    if (!confirmed) return;

    await handleSendWhatsApp({
      quotation,
      customerMobile: quotation.customer_mobile,
      customerName: quotation.customer_name || "Customer",
      docType,
      docNumber,
      amount: quotation.grand_total || 0,
      totalPieces,
      date,
    });
  };

  // ── Save / update quotation ─────────────────────────────────────────────────
  const handleSaveQuotation = async ({ quotationId, quotationNumber, billNumber = "", createdAt, customer, items, grandTotal, subtotalAmount = grandTotal, discountAmount = 0, discountPercentage = 0, packagingCharges = 0, transportCharges = 0, grossAmount = 0, cgstAmount = 0, sgstAmount = 0, igstAmount = 0, totalTaxAmount = 0, roundOffAmount = 0, sendWhatsApp = false, reset }) => {
    if (!canManageQuotations) {
      showToast("You don’t have permission to create or update quotations", B.red);
      return false;
    }
    const normalizedQuotationNumber = String(quotationNumber || "").trim();
    if (!normalizedQuotationNumber) {
      showToast("Quotation number is required", B.red);
      return false;
    }
    const normalizedCustomerName = String(customer?.customer_name || "").trim();
    if (!normalizedCustomerName) {
      showToast("Customer name is required", B.red);
      return false;
    }
    const normalizedBillNumber = String(billNumber || "").trim();
    const isBill = Boolean(normalizedBillNumber);
    if (isBill) {
      if (!String(customer?.state || "").trim()) {
        showToast("State is mandatory when a bill number is entered.", B.red);
        return false;
      }
      const normalizedGst = String(customer?.gst_number || "").trim();
      if (!normalizedGst) {
        showToast("GST number is required for a bill.", B.red);
        return false;
      }
      const gstMessage = getGSTValidationMessage(normalizedGst);
      if (gstMessage) {
        showToast(gstMessage, B.red);
        return false;
      }
    }
    const mobileMessage = getMobileValidationMessage(customer?.mobile_number);
    if (mobileMessage) {
      showToast(mobileMessage, B.red);
      return false;
    }
    if (!items?.length) {
      showToast("Add at least one design item", B.red);
      return false;
    }
    if (items.some(item => !String(item.designNo || "").trim())) {
      showToast("Please enter design number for all rows", B.red);
      return false;
    }
    if (items.some(item => Number(item.quantity) <= 0 || Number(item.price) <= 0)) {
      showToast("Each design item must have quantity > 0 and price > 0", B.red);
      return false;
    }

    setQuotationSaving(true);
    setCrudBusy?.(true);
    try {
      // Register a custom / legacy free-text transport into the transport module so
      // the very same transport is attached to the customer and the quotation.
      let transportId = customer.transport_id || "";
      let transportName = customer.transport_name || "";
      const transportGst = String(customer.transport_gst || "").trim().toUpperCase();
      const needsRegistration = transportId === CUSTOM_TRANSPORT_ID || (!transportId && String(transportName).trim());
      if (needsRegistration) {
        const rawName = getTransportName(transportName, transportGst);
        if (String(rawName || "").trim()) {
          try {
            // Uniqueness is enforced at code level: a (name + GST) duplicate —
            // or a duplicate name when GST is empty — reuses the existing
            // registry record instead of creating a second one.
            const existing = findTransportConflict(await fetchTransports(), { name: rawName, gst: transportGst });
            if (existing) {
              transportId = existing.id;
              transportName = getTransportDisplayName(existing);
            } else {
              const transportRef = await addTransport({ name: rawName, gst: transportGst, status: "active" });
              transportId = transportRef.id;
              transportName = getTransportDisplayName({ name: rawName, gst: transportGst });
            }
          } catch (e) {
            showToast("Could not register custom transport: " + (e.message || ""), B.red);
          }
        } else {
          transportId = "";
          transportName = "";
        }
      }
      // Keep the combined display string clean — the custom transport name field
      // preserves spaces while typing, so drop any outer whitespace before persisting.
      transportName = String(transportName || "").trim();

      const mergeCustomerRecord = (stored, form) => ({
        id: stored.id || "",
        customer_name: toTitleCase(form.customer_name || stored.customer_name || ""),
        address: toTitleCase(form.address || stored.address || ""),
        transport_name: toTitleCase(form.transport_name || stored.transport_name || ""),
        transport_id: form.transport_id || stored.transport_id || "",
        city: toTitleCase(form.city || stored.city || ""),
        pincode: form.pincode || stored.pincode || "",
        mobile_number: form.mobile_number || stored.mobile_number || "",
        firm_name: toTitleCase(form.firm_name || stored.firm_name || ""),
        agent_name: toTitleCase(form.agent_name || stored.agent_name || ""),
        gst_number: form.gst_number || stored.gst_number || "",
        state: form.state || stored.state || "",
        status: stored.status || form.status || "active",
      });

      let customerRecord = { ...(customer || {}), transport_id: transportId, transport_name: transportName };
      if (customerRecord.id) {
        const storedCustomer = customers.find(existing => existing.id === customerRecord.id && existing.status !== "deleted");
        const sourceCustomer = storedCustomer || customerRecord;
        customerRecord = mergeCustomerRecord(sourceCustomer, customerRecord);

        if (storedCustomer) {
          const changedFields = ["customer_name", "mobile_number", "city", "state", "pincode", "address", "transport_name", "gst_number"];
          const hasChanges = changedFields.some(field =>
            String(customerRecord[field] || "") !== String(storedCustomer[field] || "")
          );
          if (hasChanges) {
            await updateCustomer(storedCustomer.id, customerRecord);
            showToast("Customer details updated");
          }
        }
      } else {
        const existing = findExistingCustomer(customerRecord);
        if (existing) {
          customerRecord = mergeCustomerRecord(existing, customerRecord);
          const changedFields = ["customer_name", "mobile_number", "city", "state", "pincode", "address", "transport_name", "gst_number"];
          const hasChanges = changedFields.some(field =>
            String(customerRecord[field] || "") !== String(existing[field] || "")
          );
          if (hasChanges) {
            await updateCustomer(existing.id, customerRecord);
            showToast("Customer details updated");
          }
        } else {
          const newCustomerRef = await addCustomer({
            customer_name: toTitleCase(normalizedCustomerName),
            address: toTitleCase(customerRecord.address || ""),
            transport_name: toTitleCase(customerRecord.transport_name || ""),
            transport_id: customerRecord.transport_id || "",
            city: toTitleCase(customerRecord.city || ""),
            state: customerRecord.state || "",
            pincode: customerRecord.pincode || "",
            mobile_number: customerRecord.mobile_number || "",
            firm_name: toTitleCase(customerRecord.firm_name || ""),
            agent_name: toTitleCase(customerRecord.agent_name || ""),
            gst_number: customerRecord.gst_number || "",
            status: "active",
          });
          customerRecord = {
            id: newCustomerRef.id,
            customer_name: toTitleCase(normalizedCustomerName),
            address: toTitleCase(customerRecord.address || ""),
            transport_name: toTitleCase(customerRecord.transport_name || ""),
            city: toTitleCase(customerRecord.city || ""),
            state: customerRecord.state || "",
            pincode: customerRecord.pincode || "",
            mobile_number: customerRecord.mobile_number || "",
            firm_name: toTitleCase(customerRecord.firm_name || ""),
            agent_name: toTitleCase(customerRecord.agent_name || ""),
            gst_number: customerRecord.gst_number || "",
            status: "active",
          };
          showToast("Customer created and linked to quotation");
        }
      }

      const quotationDate = createdAt ? new Date(createdAt) : new Date();
      const payload = {
        quotation_number: normalizedQuotationNumber,
        bill_number: String(billNumber || "").trim(),
        quotation_year: quotationDate.getFullYear(),
        created_at: quotationDate,
        customer_id: customerRecord.id || "",
        customer_name: toTitleCase(customerRecord.customer_name || normalizedCustomerName),
        customer_mobile: customerRecord.mobile_number || "",
        customer_city: toTitleCase(customerRecord.city || ""),
        customer_state: customerRecord.state || "",
        customer_pincode: customerRecord.pincode || "",
        customer_gst: customerRecord.gst_number || "",
        transport_name: customerRecord.transport_name || "",
        transport_id: customerRecord.transport_id || "",
        items: items.map(item => ({
          serial: item.serial,
          design_id: item.designId,
          design_no: item.designNo,
          quantity: item.quantity,
          price: item.price,
          subtotal: item.subtotal,
        })),
        subtotal_amount: Number(subtotalAmount || 0),
        discount_amount: Number(discountAmount || 0),
        discount_percentage: Number(discountPercentage || 0),
        packaging_charges: Number(packagingCharges || 0),
        transport_charges: Number(transportCharges || 0),
        gross_amount: Number(grossAmount || 0),
        cgst_amount: Number(cgstAmount || 0),
        sgst_amount: Number(sgstAmount || 0),
        igst_amount: Number(igstAmount || 0),
        total_tax_amount: Number(totalTaxAmount || 0),
        round_off_amount: Number(roundOffAmount || 0),
        grand_total: grandTotal,
        status: "draft",
      };
      if (quotationId) {
        await updateQuotation(quotationId, payload);
        await logActivity(profile?.uid, "quotation_update", `Quotation updated for ${customerRecord.customer_name || "customer"}`);
        showToast("Quotation updated");
        setEditQuotation(null);
      } else {
        await addQuotation(payload);
        await logActivity(profile?.uid, "quotation_create", `Quotation created for ${customerRecord.customer_name || "customer"}`);
        showToast("Quotation created");
        const savedQuotation = {
          ...payload,
          created_at: quotationDate,
          customer_address: customerRecord.address || "",
        };
        // Always offer to print the quotation after a successful save,
        // regardless of whether a transport name was entered. The transport
        // slip stays available via the dedicated 📦 Slip button in the list.
        setQuotationPrintPrompt(savedQuotation);

        if (sendWhatsApp) {
          const hasBillNumber = Boolean(String(billNumber || "").trim());
          const docType = hasBillNumber ? "Invoice" : "Quotation";
          const docNumber = hasBillNumber ? String(billNumber || "").trim() : String(quotationNumber || "").trim();
          const totalPieces = (payload.items || []).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
          const date = new Date().toLocaleDateString("en-GB");

          handleSendWhatsApp({
            quotation: {
              ...payload,
              id: "",
              created_at: new Date(),
              customer_address: customerRecord.address || "",
            },
            customerMobile: customerRecord.mobile_number || "",
            customerName: customerRecord.customer_name || normalizedCustomerName,
            docType,
            docNumber,
            amount: grandTotal,
            totalPieces,
            date,
          });
        }
      }
      loadQuotations();
      syncQuotationCounters(payload);
      reset?.();
      return true;
    } catch (e) {
      showToast(e.message, B.red);
      return false;
    } finally {
      setQuotationSaving(false);
      setCrudBusy?.(false);
    }
  };

  const handleSaveQuotationSettings = async (nextSettings) => {
    setSettingsSaving(true);
    setCrudBusy?.(true);
    try {
      await saveQuotationSettings(nextSettings);
      showToast("Settings saved");
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setSettingsSaving(false);
      setCrudBusy?.(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────
  const handleDeleteQuotation = async (quotation) => {
    if (!canManageQuotations) {
      showToast("You don’t have permission to delete quotations", B.red);
      return;
    }
    const confirmed = await confirm({
      title: "Delete quotation?",
      message: `Quotation "${quotation.quotation_number}" will be permanently deleted.`,
      confirmLabel: "Delete Quotation",
    });
    if (!confirmed) return;
    setCrudBusy?.(true);
    try {
      await deleteQuotation(quotation.id);
      loadQuotations();
      syncQuotationCounters(null);
      setViewQuotation(prev => prev?.id === quotation.id ? null : prev);
      setEditQuotation(prev => prev?.id === quotation.id ? null : prev);
      showToast("Quotation deleted", B.red);
    } catch (e) {
      showToast(e.message, B.red);
    } finally {
      setCrudBusy?.(false);
    }
  };

  // ── Print ───────────────────────────────────────────────────────────────────
  const handlePrintQuotation = (quotation) => {
    printQuotationDocument({
      quotation,
      customers,
      designs,
      branding,
      showPackagingCharges: Boolean(quotationSettings?.showPackagingCharges),
      onError: (message) => showToast(message, B.red),
    });
  };

  const handlePrintTransportSlip = (quotation) => {
    printTransportSlip({
      quotation,
      customers,
      branding,
      onError: (message) => showToast(message, B.red),
    });
  };

  const handlePrintPromptClose = () => setQuotationPrintPrompt(null);

  return {
    quotations,
    quotationsLoading,
    quotationNumbers,
    quotationSettings,
    quotationSaving,
    settingsSaving,
    whatsappSending,
    upiQrSending,
    canManageQuotations,
    viewQuotation, setViewQuotation,
    editQuotation, setEditQuotation,
    quotationPrintPrompt,
    handlePrintPromptClose,
    quotationSearch, setQuotationSearch,
    quotationCustomerFilter, setQuotationCustomerFilter,
    quotationTypeFilter, setQuotationTypeFilter,
    quotationDateFrom, setQuotationDateFrom,
    quotationDateTo, setQuotationDateTo,
    quotationPage, setQuotationPage,
    filteredQuotations, paginatedQuotations,
    quotationTotalPages,
    quotationPageSize: QUOTATION_PAGE_SIZE,
    handleSaveQuotation,
    handleSaveQuotationSettings,
    handleDeleteQuotation,
    handlePrintQuotation,
    handlePrintTransportSlip,
    handleSendWhatsApp,
    handleSendWhatsAppFromList,
    handleSendUpiQr,
    handleSendUpiQrFromList,
    formatDateTime,
    formatCurrency,
    getDateInputValue,
  };
}
